import React, { useCallback, useEffect, useState, useRef } from 'react';
import { AppState } from 'store';
import { Button, Col, message, Row, UploadProps, Upload } from 'antd';
import type { RcFile, UploadChangeParam } from 'antd/es/upload';
import type { UploadFile } from 'antd/es/upload/interface';
import ButtonFooter from 'components/ButtonFooter/ButtonFooter';
import NavStep from 'components/NavStep/NavStep';
import { navStep } from 'constants/nav';
import { HTML5Backend } from 'react-dnd-html5-backend';
import DragTable from 'components/DragTable/DragTable';
import { DndProvider } from 'react-dnd';
import { TTemplateState } from 'modules/template/template.reducer';
import update from 'immutability-helper';
import { ColumnTypes, TStages, TStep2 } from 'types/template';
import { IndexedObject } from 'constants/types';
import DraggableBodyRow from 'components/EditTableColRow/DragTableRow';
import EditTemplate from 'hook/EditTemplate';
import { getServerImage } from 'libs/helpers/functions';
import { omit } from 'lodash';
import { MESSAGE_ERROR_ADD_QUESTION_EVENT, MESSAGE_ERROR_FILE_TOO_LARGE } from 'constants/notifications/message';
import { MinusCircleFilled, PlusCircleFilled, SwapOutlined } from '@ant-design/icons';
import { EPath } from 'constants/routes';
import { TGroupsClassDetail } from 'types/groupsClass';
import ConfirmModal from 'components/CommonModal/ConfirmModal';
import UploadCsvModal from 'components/CommonModal/UploadCsvModal';
import UploadImage from 'components/UploadImage/UploadImage';
import { Link } from 'react-router-dom';
import CustomDivider from 'components/CustomDivider/CustomDivider';
import useGetTemplateLs from 'hook/useGetTemplateLs';
import { useDispatch, useSelector } from 'react-redux';
import { uploadModule } from 'modules/upload/upload.reducer';
import { messageModule } from 'modules/message/message.reducer';
import { MAX_SIZE_IMAGE } from 'constants/common';
import EditableCellStep3 from './EditTableCellStep3';
import { INITIAL_DATA, TStep3State } from './AdmTempEvent.state';
import styles from './style.module.scss';

type TProps = {
  template: TTemplateState;
  currentGroup: TGroupsClassDetail;
  updateTemplate: (
    data: TStep3State,
    id: number,
    groupId: number,
    buttonFooterNext?: boolean,
    buttonFooterPre?: boolean,
  ) => void;
  saveTemplate: (data: TTemplateState) => void;
  uploadCsvQuestionFile: (data: { file: FormData; id: number; groupId: number; step: number }) => void;
  downloadCsvQuestionFile: (data: { id: number; groupId: number; step: number }) => void;
  uploadQuestionImage: (data: { file: FormData; id: number; groupId: number; step: number; record: TStep2 }) => void;
};

const AdmTempEvent = ({
  template,
  updateTemplate,
  currentGroup,
  uploadCsvQuestionFile,
  saveTemplate,
  downloadCsvQuestionFile,
  uploadQuestionImage,
}: TProps) => {
  const dispatch = useDispatch();
  const [data, setData] = useState<TStep2[]>(INITIAL_DATA);
  const [openModal, setOpenModal] = useState<'confirm' | 'upload' | ''>('');
  const [dataRow, setDataRow] = useState<IndexedObject>({});
  const [stateSelect, setStateSelect] = useState<TStages[]>([]);
  const [dataTemp] = useGetTemplateLs();
  const [newFilesToUpload, setNewFilesToUpload] = useState<Record<string, RcFile>>({});
  const [currentFileLists, setCurrentFileLists] = useState<Record<string, UploadFile[]>>({});
  // アップロードされた画像のファイル名が Redux store に反映されたレコードIDを追跡する状態
  const [reflectedImageIds, setReflectedImageIds] = useState<Set<string>>(new Set());
  // 「進む」「戻る」ボタンがクリックされたことを示すフラグ
  const [isFooterButtonClicked, setIsFooterButtonClicked] = useState(false);
  // 最終的な updateTemplate 呼び出しの引数を保持する状態
  const [buttonFooterArgs, setButtonFooterArgs] = useState<{ next: boolean; pre: boolean } | null>(null);
  const lastUploadedImage = useSelector((state: AppState) => state.pages.upload.lastUploadedImage);

  // コンポーネント全体のマウント状態を追跡するref
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  EditTemplate();

  useEffect(() => {
    if (template.id && template.step3 && template.step3.length > 0) {
      setData(template.step3);
      // template.step2 から currentFileLists を初期化
      const initialCurrentFileLists: Record<string, UploadFile[]> = {};
      template.step3.forEach((q) => {
        if (q.imageName) {
          initialCurrentFileLists[q.id as string] = [
            {
              uid: q.id as string, // 一意のID
              name: q.imageName,
              status: 'done',
              url: getServerImage(q.imageName), // 既存画像のURL
            },
          ];
        } else {
          initialCurrentFileLists[q.id as string] = [];
        }
      });
      setCurrentFileLists(initialCurrentFileLists);
    }
    if (template.id && template.step3 && template.step3.length === 0) {
      const updateInitData = INITIAL_DATA.map((d, index) => {
        if (index < 3) {
          return {
            ...d,
            // stageName: template.step1?.stages[0].name ?? '',
          };
        }
        if (index < 5) {
          return {
            ...d,
            // stageName: template.step1?.stages[1].name ?? '',
          };
        }
        return {
          ...d,
          // stageName: template.step1?.stages[2].name ?? '',
        };
      });
      setData(updateInitData);
      setCurrentFileLists({}); // 初期データがない場合はファイルリストもクリア
    }
    setStateSelect(template.step1?.stages ?? []);
  }, [template]);

  useEffect(() => {
    if (Object.keys(dataTemp).length > 0) {
      saveTemplate(dataTemp);
    }
  }, []);

  const handleUploadImageChange = useCallback(
    (recordId: string, info: UploadChangeParam) => {
      if (!mounted.current) {
        return;
      }

      setCurrentFileLists((prev) => ({ ...prev, [recordId]: info.fileList }));

      const newFileListForRecord = info.fileList;
      if (newFileListForRecord.length > 0 && newFileListForRecord[0].originFileObj) {
        // 新しいファイルが選択された場合
        setNewFilesToUpload((prev) => ({ ...prev, [recordId]: newFileListForRecord[0].originFileObj as RcFile }));
        // 新しいファイルが選択された場合、data state の imageName は undefined に設定
        setData((prevData) =>
          prevData.map((item) => (item.id === recordId ? { ...item, imageName: undefined } : item)),
        );
      } else if (newFileListForRecord.length === 0) {
        // ファイルが削除された場合
        setNewFilesToUpload((prev) => {
          const updated = { ...prev };
          delete updated[recordId];
          return updated;
        });
        // ファイルが削除された場合、data state の imageName も undefined に更新
        setData((prevData) =>
          prevData.map((item) => (item.id === recordId ? { ...item, imageName: undefined } : item)),
        );
      }
    },
    [mounted, setCurrentFileLists, setNewFilesToUpload, setData],
  );

  const defaultColumns: (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] = [
    {
      title: '',
      dataIndex: 'swap',
      width: '1%',
      render: () => (!template.isLinkedWithTraining ? <SwapOutlined rotate={90} /> : <></>),
    },
    {
      title: '',
      dataIndex: 'ordinal',
      align: 'center',
      width: '1%',
      render: (_: any, record: TStep2) => <>{data.findIndex((item) => item.id === record.id) + 1}</>,
    },
    {
      title: 'カテゴリ',
      dataIndex: 'categoryName',
      width: '8%',
      editable: true,
    },
    {
      title: '問題',
      dataIndex: 'question',
      width: '10%',
      editable: true,
    },
    {
      title: '選択肢1',
      dataIndex: 'option1',
      width: '10%',
      render: (_: any, record: TStep2) => record.options[0]?.value,
      editable: true,
    },
    {
      title: '選択肢2',
      dataIndex: 'option2',
      width: '10%',
      render: (_: any, record: TStep2) => record.options[1]?.value,
      editable: true,
    },
    {
      title: '選択肢3',
      dataIndex: 'option3',
      width: '10%',
      render: (_: any, record: TStep2) => record.options[2]?.value,
      editable: true,
    },
    {
      title: '選択肢4',
      dataIndex: 'option4',
      width: '10%',
      render: (_: any, record: TStep2) => record.options[3]?.value,
      editable: true,
    },
    {
      title: '正解',
      dataIndex: 'correctAnswer',
      width: '1%',
      editable: true,
      render: (_: any, record: TStep2) => record.options.find((item) => item.isCorrect)?.index,
    },
    {
      title: '配点',
      dataIndex: 'score',
      width: '1%',
      editable: true,
    },
    {
      title: '解説',
      dataIndex: 'comment',
      width: '10%',
      editable: true,
    },
    {
      title: '画像　※任意',
      dataIndex: 'imageName',
      width: '10%',
      render: (_: any, record: TStep2) => {
        const recordId = record.id as string;

        // 各レコードの現在のファイルリストを取得
        const currentFilesForRecord = currentFileLists[recordId] || [];
        // UploadImage に渡す handleChange 関数を定義
        const handleUploadChangeForRecord: UploadProps['onChange'] = (info) => {
          handleUploadImageChange(recordId, info);
        };

        const uploadProps: UploadProps = {
          onRemove: (file) => {
            // currentFileLists から削除
            setCurrentFileLists((prev) => {
              const updatedFiles = prev[recordId]?.filter((item) => item.uid !== file.uid) || [];
              return { ...prev, [recordId]: updatedFiles };
            });
            // newFilesToUpload からも削除
            setNewFilesToUpload((prev) => {
              const updated = { ...prev };
              if (file.originFileObj && updated[recordId] && updated[recordId].uid === file.uid) {
                delete updated[recordId];
              }
              return updated;
            });
            setData((prevData) =>
              prevData.map((item) => (item.id === recordId ? { ...item, imageName: undefined } : item)),
            );
          },
          beforeUpload: (file) => {
            const isLtMaxSize = file.size < MAX_SIZE_IMAGE;
            if (!isLtMaxSize) {
              dispatch(messageModule.actions.setError(MESSAGE_ERROR_FILE_TOO_LARGE));
              return Upload.LIST_IGNORE;
            }
            setNewFilesToUpload((prev) => ({ ...prev, [recordId]: file }));
            setData((prevData) =>
              prevData.map((item) => (item.id === recordId ? { ...item, imageName: undefined } : item)),
            );
            return false; // Ant Design の Upload が自動的にアップロードしないようにする
          },
        };

        return (
          <div>
            <UploadImage
              key={recordId} // 一意のキー
              maxCount={1}
              uploadText="画像をアップロード"
              handleChange={handleUploadChangeForRecord}
              props={uploadProps}
              fileList={currentFilesForRecord}
            />
            {(() => {
              const hasExistingImageInRecord = !!record.imageName;
              const isNewFileSelected = !!newFilesToUpload[recordId]; // newFilesToUpload にファイルがあるか

              // 既存の画像がなく、新しいファイルも選択されていない場合、かつ currentFilesForRecord も空の場合
              if (!hasExistingImageInRecord && !isNewFileSelected && currentFilesForRecord.length === 0) {
                return <div>なし</div>;
              }
              return null;
            })()}
          </div>
        );
      },
    },
    {
      title: '',
      dataIndex: 'operation',
      width: '3%',
      render: (_: any, record: IndexedObject) =>
        data.length > 1 && !template.isLinkedWithTraining ? (
          <div
            className={styles.deleteRowButton}
            onClick={() => {
              setDataRow(record);
              setOpenModal('confirm');
            }}
          >
            <MinusCircleFilled className={styles.iconRemove} />
          </div>
        ) : (
          <></>
        ),
    },
  ];

  const handleMoveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      if (!template.isLinkedWithTraining) {
        const dragRow = data[dragIndex];
        setData(
          update(data, {
            $splice: [
              [dragIndex, 1],
              [hoverIndex, 0, dragRow],
            ],
          }),
        );
      }
    },
    [data],
  );
  const handleDelete = () => {
    const newData = data.filter((item) => item.id !== dataRow.id);
    setData(newData);
    setCurrentFileLists((prev) => {
      const updated = { ...prev };
      delete updated[dataRow.id as string];
      return updated;
    });
    setNewFilesToUpload((prev) => {
      const updated = { ...prev };
      delete updated[dataRow.id as string];
      return updated;
    });
  };

  const handleAdd = () => {
    const newId = `${Date.now()}`;
    const newData: TStep2 = {
      id: newId,
      categoryName: '',
      question: '設問を入力してください。',
      options: [
        { index: 1, value: 'Answer', isCorrect: false },
        { index: 2, value: 'Answer', isCorrect: false },
        { index: 3, value: 'Answer', isCorrect: true },
        { index: 4, value: 'Answer', isCorrect: false },
      ],
      comment: 'comment',
      score: 5,
    };
    if (data.length < 12) {
      setData([...data, newData]);
      setCurrentFileLists((prev) => ({ ...prev, [newId]: [] })); // 新しい行のファイルリストを初期化
    } else {
      message.error(MESSAGE_ERROR_ADD_QUESTION_EVENT);
    }
  };

  const handleSave = (row: TStep2) => {
    const newData = data.map((data) => (data.id === row.id ? row : data));

    setData(newData);
  };

  const components = {
    body: {
      row: DraggableBodyRow,
      cell: (props: any) => <EditableCellStep3 {...props} stages={stateSelect} />,
    },
  };

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: TStep2) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const handleFooterButtonClick = async (next: boolean, pre: boolean) => {
    // 「進む」「戻る」ボタンが押されたことを示すフラグをセット
    setIsFooterButtonClicked(true);

    // newFilesToUpload が空の場合（画像アップロードがない場合）は、
    // 画像以外のデータ変更や画像削除を保存
    const newStep3 = data.map((d) => {
      const recordId = d.id as string;
      let updatedImageName = d.imageName;

      const currentFilesForRecord = currentFileLists[recordId] || [];
      const hasNewFileSelected = !!newFilesToUpload[recordId]; // 新しいファイルが選択されているか
      const hasExistingFileInUpload = currentFilesForRecord.length > 0 && !currentFilesForRecord[0].originFileObj; // UploadImageに既存のファイルが表示されているか

      if (hasNewFileSelected) {
        // 新しいファイルが選択されている場合、既存のimageNameはクリアされる
        updatedImageName = undefined;
      } else if (d.imageName && !hasExistingFileInUpload) {
        // 元々画像があったが、UploadImageに既存のファイルが表示されていない場合（削除されたと判断）
        updatedImageName = undefined;
      } else if (d.imageName && hasExistingFileInUpload) {
        // 元々画像があり、UploadImageにも表示されている場合（変更なし）
        updatedImageName = d.imageName;
      } else {
        // 元々画像がなく、新しいファイルも選択されていない場合
        updatedImageName = undefined;
      }
      return omit({ ...d, imageName: updatedImageName }, ['id']);
    });
    const newData: TStep3State = { step3: newStep3 };

    // アップロードするファイルがない場合
    if (Object.keys(newFilesToUpload).length === 0) {
      updateTemplate(newData, template.id, currentGroup.id ?? 0, next, pre);
      setNewFilesToUpload({});
      setReflectedImageIds(new Set());
      setIsFooterButtonClicked(false); // 「進む」ボタンのフラグをリセット
      setButtonFooterArgs(null); // 処理完了後に引数をリセット
      return;
    }

    // アップロードが必要なファイルがある場合
    setReflectedImageIds(new Set()); // 新しいアップロードサイクルの開始時にクリア

    // 新しいファイルのアップロード処理
    const uploadPromises = Object.keys(newFilesToUpload).map(async (recordId) => {
      const file = newFilesToUpload[recordId];
      const recordToUpload = data.find((d) => (d.id as string) === recordId); // レコードを検索
      if (file && recordToUpload) {
        const formData = new FormData();
        formData.append('file', file);
        await uploadQuestionImage({
          file: formData,
          id: template.id,
          groupId: currentGroup.id ?? 0,
          step: 3,
          record: recordToUpload,
        });
      }
    });

    await Promise.all(uploadPromises);
  };

  const handleNextPage = async () => {
    const next = true;
    const pre = false;
    await handleFooterButtonClick(next, pre);
    // 全てのアップロードが完了したら、useEffect が最終的な updateTemplate を呼び出すための引数をセット
    setButtonFooterArgs({ next, pre });
  };

  const handleBackPage = async () => {
    const next = false;
    const pre = true;
    await handleFooterButtonClick(next, pre);
    // 全てのアップロードが完了したら、useEffect が最終的な updateTemplate を呼び出すための引数をセット
    setButtonFooterArgs({ next, pre });
  };

  // reflectedImageIds の変更を監視し、全ての画像が反映されたら updateTemplate を呼び出す
  useEffect(() => {
    // 「進む」ボタンが押されていない、または buttonFooterArgs がセットされていない場合は何もしない
    if (!isFooterButtonClicked || !buttonFooterArgs) {
      // buttonFooterArgs のチェックを追加
      return;
    }

    // アップロードするファイルが元々ない、または全て削除された場合は何もしない
    if (Object.keys(newFilesToUpload).length === 0) {
      if (reflectedImageIds.size > 0) {
        setReflectedImageIds(new Set());
      }
      return;
    }

    // 全てのアップロードされた画像のファイル名が reflectedImageIds に反映されているかを確認
    if (reflectedImageIds.size === Object.keys(newFilesToUpload).length) {
      // ローカルの `data` ステートが既に最新の `imageName` を持っているため、そのまま使用
      const finalStep3 = data.map((localItem) => omit(localItem, ['id']));

      const finalData: TStep3State = {
        step3: finalStep3,
      };

      updateTemplate(finalData, template.id, currentGroup.id ?? 0, buttonFooterArgs.next, buttonFooterArgs.pre);
      setNewFilesToUpload({});
      setReflectedImageIds(new Set());
      setIsFooterButtonClicked(false);
      setButtonFooterArgs(null);
    }
  }, [
    reflectedImageIds,
    isFooterButtonClicked,
    newFilesToUpload,
    data,
    template.id,
    currentGroup.id,
    updateTemplate,
    buttonFooterArgs,
  ]);

  // lastUploadedImage の変更を監視し、data ステートと reflectedImageIds を更新
  useEffect(() => {
    if (!isFooterButtonClicked || !lastUploadedImage) {
      return;
    }

    // data ステートを更新
    setData((prevData) =>
      prevData.map((item) =>
        item.id === lastUploadedImage.recordId ? { ...item, imageName: lastUploadedImage.name } : item,
      ),
    );

    // reflectedImageIds を更新
    setReflectedImageIds((prev) => {
      const newSet = new Set(prev);
      newSet.add(lastUploadedImage.recordId);
      return newSet;
    });

    // 処理後、lastUploadedImage をクリアして重複処理を防ぐ
    dispatch(uploadModule.actions.clearLastUploadedImage());
  }, [lastUploadedImage, isFooterButtonClicked, dispatch]);

  const handleDownloadFile = () => {
    if (currentGroup.id) downloadCsvQuestionFile({ groupId: currentGroup.id, id: template.id, step: 3 });
  };
  return (
    <DndProvider backend={HTML5Backend}>
      <ConfirmModal
        open={openModal === 'confirm'}
        onClose={() => setOpenModal('')}
        title="確認"
        message="問題を削除してよろしいでしょうか？"
        onConfirm={handleDelete}
      />
      <UploadCsvModal
        uploadFile={uploadCsvQuestionFile}
        open={openModal === 'upload'}
        closeModal={() => setOpenModal('')}
        groupId={currentGroup.id ?? 0}
        id={template.id}
        step={3}
      />
      <div className={styles.assignmentsQuestionWrap}>
        <Row>
          <Col span={12}>
            <Row>
              <p>研修テンプレート登録</p>
            </Row>
            <Row className={styles.assignmentsQuestionWrap__marginTop}>
              <Link to={EPath.A3}>
                <Button>テンプレート一覧に戻る</Button>
              </Link>
            </Row>
          </Col>
        </Row>
        <NavStep data={navStep} />
        <div className={styles.contentTitle}>栽培イベント問題の設定</div>
        <div className={styles.content}>
          <div className={styles.contentWrap}>
            <div className={styles.form}>
              <p>栽培イベント問題とは、環境制御に関する突発的な出来事に対する問題です。</p>
              <p>カテゴリ・問題文・選択肢・正解番号・配点解説を設定できます。</p>
              <p>栽培イベント問題を最大12個まで登録できます。</p>
              <br />
              <p className={styles.pForm}>基礎問題設定 基本問題</p>
              <CustomDivider />
              <Row gutter={[20, 20]} className={styles.csvBtn}>
                {!template.isLinkedWithTraining && (
                  <Col>
                    <Button onClick={() => setOpenModal('upload')}>CSV一括入力</Button>
                  </Col>
                )}
                <Col>
                  <Button onClick={handleDownloadFile}>CSV一括出力</Button>
                </Col>
              </Row>
              <DragTable data={data} moveRow={handleMoveRow} columns={columns} components={components} />
              {data.length < 12 && !template.isLinkedWithTraining && (
                <Button type="dashed" onClick={handleAdd} className={styles.buttonAdd}>
                  <PlusCircleFilled className={styles.iconAdd} />
                </Button>
              )}
            </div>
            <div className={styles.heightBottom} />
          </div>
        </div>
      </div>
      <ButtonFooter handleClickPre={handleBackPage} handleClickNext={handleNextPage} />
    </DndProvider>
  );
};

export default AdmTempEvent;
